import { useState } from 'react'
import { UploadOutlined } from '@ant-design/icons'
import { StyledModal } from '../styledComponents'
import { Button, message, Upload, notification } from 'antd'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import Spinner from '$components/Spinner'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import {
  postMultipleParts,
  putPartsToGroup,
  fetchClientMaterials,
  createClientMaterial,
  validateExcelFile,
} from '$api/client'
import { useRefetchTimer } from '$context/RefetchTimerProvider'
import INTERNAL_PROPERTIES from './internalProperties'
import MappingForm from './MappingForm'
import DuplicateSelectionModal from './DuplicateSelectionModal'
import { isDemoError } from '$utils/demo'

const readExcelHeaders = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
      resolve({
        headers: rows.length > 0 ? Object.keys(rows[0]) : [],
        rows,
      })
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })

const UploadExcelButton = ({ groups, activeGroup }) => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)

  const [pendingFile, setPendingFile] = useState(null)
  const [localHeaders, setLocalHeaders] = useState([])
  const [localRows, setLocalRows] = useState([])

  const [validationResult, setValidationResult] = useState(undefined)
  const [lastMapping, setLastMapping] = useState(undefined)

  const queryClient = useQueryClient()
  const { startRefetch, stopRefetch } = useRefetchTimer()

  const handleUpload = async (file) => {
    try {
      setLoading(true)
      const { headers, rows } = await readExcelHeaders(file)
      setPendingFile(file)
      setLocalHeaders(headers)
      setLocalRows(rows)
      setIsModalOpen(true)
    } catch {
      message.error(t('parts_master_data.upload.file_read_error'))
    } finally {
      setLoading(false)
    }
  }

  const handleMappingOk = async (mappingFormValues) => {
    setIsModalOpen(false)

    if (typeof mappingFormValues.group === 'object') {
      mappingFormValues.group = mappingFormValues.group?.value
    }

    const normalizedMappingFormVaues = Object.fromEntries(
      Object.entries(mappingFormValues).map(([key, value]) => [
        key,
        typeof value === 'string' ? value.replaceAll('\r', '').trim() : value,
      ]),
    )

    setLastMapping(normalizedMappingFormVaues)

    const config = buildConfig(normalizedMappingFormVaues)

    try {
      setLoading(true)
      const result = await validateExcelFile(pendingFile, config)
      setValidationResult(result)

      if (result.skipped_parts.length > 0) {
        notification.warning({
          message: t('parts_master_data.upload.parts_skipping_message_header'),
          description: result.skipped_parts.join(', '),
          placement: 'top',
        })
      }

      if (result.duplicate_count > 0) {
        setIsDuplicateModalOpen(true)
      } else {
        await uploadParts(result.unique_rows, normalizedMappingFormVaues)
      }
    } catch {
      message.error(t('parts_master_data.upload.file_read_error'))
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicateSelection = async (selectedDuplicates) => {
    setIsDuplicateModalOpen(false)
    const rows = [
      ...validationResult.unique_rows,
      ...Object.values(selectedDuplicates),
    ]
    await uploadParts(rows, lastMapping)
  }

  const uploadParts = async (rows, mappingFormValues) => {
    setLoading(true)
    const parts = await getPartsFromExcel(mappingFormValues, rows)
    const partsWithGroup = parts.map((part) => ({
      ...part,
      group: mappingFormValues.group,
    }))
    addPartsMutation.mutate(partsWithGroup, {
      onSuccess: (response) => {
        notification.success({
          message: t('parts_master_data.upload.success_excel_upload', {
            count1: response.data.created_parts.length,
            count2: response.data.updated_parts.length,
          }),
          description: '',
          placement: 'top',
        })
        if (response.data.fc_reset_parts.length > 0) {
          notification.warning({
            message: t('parts_master_data.upload.fc_reset_after_excel_upload'),
            description: response.data.fc_reset_parts.join(', '),
            placement: 'top',
          })
        }
      },
    })
    startRefetch(
      500,
      () => {
        queryClient.refetchQueries(['allpartspaginated'])
        queryClient.invalidateQueries(['parts'])
      },
      0,
    )
  }

  const buildConfig = (mappingFormValues) => {
    const fieldMap = {
      id_part_client: 'id_part_client',
      name: 'name',
      Height: 'height',
      Width: 'width',
      Depth: 'depth',
      Weight: 'weight',
      Price: 'price',
      'Price AM': 'price_am',
      'Internal Material': 'internal_material',
    }

    const config = {
      number_separator: mappingFormValues.number_separator ?? 'point_decimal',
    }

    for (const [formKey, configKey] of Object.entries(fieldMap)) {
      if (mappingFormValues[formKey]) {
        config[configKey] = mappingFormValues[formKey]
      }
    }

    return config
  }

  useQuery(
    ['clientMaterialsForExcelUpload'],
    () =>
      fetchClientMaterials({
        queryKey: [
          'clientMaterialsForExcelUpload',
          { page: 1, size: 100, sort_by: 'name' },
        ],
      }),
    {
      enabled: isModalOpen,
      staleTime: 5 * 60 * 1000,
    },
  )

  const addPartsMutation = useMutation({
    mutationFn: postMultipleParts,
    onSuccess: (response, variables) => {
      stopRefetch()

      if (variables[0].group) {
        addToGroupMutation.mutate({
          partsIds: [
            ...response.data.created_parts,
            ...response.data.updated_parts,
            ...response.data.unchanged_parts,
          ],
          groupId: variables[0].group,
        })
      } else {
        setLoading(false)
        queryClient.refetchQueries(['allpartspaginated'])
        queryClient.invalidateQueries(['parts'])
      }
    },
    onError: (err) => {
      setLoading(false)
      console.error(err)
      stopRefetch()
      if (!isDemoError(err)) {
        message.error(`there was an error!`)
      }
    },
  })

  const addToGroupMutation = useMutation({
    mutationFn: putPartsToGroup,
    onSuccess: () => {
      setLoading(false)
      queryClient.refetchQueries(['allpartspaginated'])
      queryClient.invalidateQueries(['parts'])
    },
    onError: (err) => {
      setLoading(false)
      console.error(err)
      if (!isDemoError(err)) {
        message.error(`there was an error!`)
      }
    },
  })

  const getPartsFromExcel = async (mappingFormValues, rows) => {
    const cachedMaterials = queryClient.getQueryData([
      'clientMaterialsForExcelUpload',
    ])
    const sessionMaterialCache = new Map(
      cachedMaterials?.items?.map((m) => [m.name.toLowerCase(), m.id]) ?? [],
    )

    const resolveMaterialId = async (materialName) => {
      if (!materialName) return null
      const key = String(materialName).trim().toLowerCase()
      if (!key) return null

      if (sessionMaterialCache.has(key)) return sessionMaterialCache.get(key)

      try {
        const newMaterial = await createClientMaterial({ name: materialName })
        notification.success({ message: t('new_material_recognized') })
        sessionMaterialCache.set(key, newMaterial.id)
        queryClient.setQueryData(['clientMaterialsForExcelUpload'], (old) => ({
          ...old,
          items: [...(old?.items ?? []), newMaterial],
        }))
        return newMaterial.id
      } catch (error) {
        console.error(`Failed to create material: ${materialName}`, error)
        return null
      }
    }

    const uniqueMaterialNames = [
      ...new Set(
        rows
          .map((row) => row[mappingFormValues['Internal Material']])
          .filter(Boolean),
      ),
    ]

    for (const materialName of uniqueMaterialNames) {
      await resolveMaterialId(materialName)
    }

    return rows.map((row) => {
      const partToAdd = {
        id_part_client: String(row[mappingFormValues.id_part_client]),
        name: row[mappingFormValues.name],
        attributes: [],
        float_attributes: [],
        status_attributes: [],
        dwh_materials: [],
      }

      const materialName = row[mappingFormValues['Internal Material']]
      if (materialName) {
        const key = String(materialName).trim().toLowerCase()
        const clientMaterialId = sessionMaterialCache.get(key)
        if (clientMaterialId) partToAdd.client_material_id = clientMaterialId
      }

      for (const prop of INTERNAL_PROPERTIES.filter((p) => p.float_attribute)) {
        const value = row[mappingFormValues[prop.name]]
        if (typeof value === 'number') {
          partToAdd.float_attributes.push({
            key: prop.name,
            value: value * (mappingFormValues[`${prop.name}-unit`] ?? 1),
          })
        }
      }

      for (const prop of INTERNAL_PROPERTIES.filter((p) => p.attribute)) {
        const value = row[mappingFormValues[prop.name]]
        if (value) partToAdd.attributes.push({ key: prop.name, value })
      }

      return partToAdd
    })
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setValidationResult(null)
    setPendingFile(null)
    setLocalHeaders([])
    setLocalRows([])
  }

  const handleDuplicateCancel = () => {
    setIsDuplicateModalOpen(false)
  }

  if (loading) {
    return (
      <div
        style={{
          textAlign: 'center',
          marginLeft: 'auto',
          marginRight: 'auto',
          verticalAlign: 'middle',
          padding: '25% 0',
        }}
      >
        <Spinner />
        {t('parts_master_data.upload.upload_in_progress')}
      </div>
    )
  }

  return (
    <>
      <Upload
        name="file"
        accept=".xlsx"
        maxCount="1"
        beforeUpload={(file) => {
          const isLt5M = file.size / 1024 / 1024 < 5
          if (!isLt5M) {
            message.error(t('parts_master_data.upload.file_size_error'))
            return Upload.LIST_IGNORE
          }
          handleUpload(file)

          // always return false to disable default upload as we are handling that differently
          return false
        }}
      >
        <Button
          icon={<UploadOutlined />}
          type="primary"
          size="large"
          style={{ borderRadius: '10px 0 0 10px' }}
        >
          {t('parts_master_data.upload.upload_button')}
        </Button>
      </Upload>
      <StyledModal
        destroyOnClose="true"
        centered="true"
        title={t('parts_master_data.upload.mapping_modal_title')}
        open={isModalOpen}
        onCancel={handleCancel}
        maskClosable={false}
        footer={[
          <Button
            type="primary"
            form="mapDataForm"
            key="submit"
            htmlType="submit"
          >
            {t('button.submit')}
          </Button>,
        ]}
      >
        <MappingForm
          name="mapDataForm"
          internalProperties={INTERNAL_PROPERTIES}
          propertiesToMap={localHeaders}
          handleOk={handleMappingOk}
          data={localRows}
          groups={groups}
          activeGroup={activeGroup}
        />
      </StyledModal>

      <DuplicateSelectionModal
        visible={isDuplicateModalOpen}
        onCancel={handleDuplicateCancel}
        duplicateRows={validationResult?.duplicate_rows || {}}
        duplicatePartNos={validationResult?.duplicate_part_nos || []}
        mapping={lastMapping}
        onConfirm={handleDuplicateSelection}
      />
    </>
  )
}

export default UploadExcelButton
